import { Application } from '@modules/common/types/application';
import { BaseStitchListStateWithSearch } from '@modules/common/types/BaseStitchListStateWithSearch';
import { Like } from '@modules/common/types/like';
import { SortOrder } from '@modules/common/types/sort-order';
import { type SortBy } from '@modules/common/types/SortBy';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { AdvancedSearchState } from '@modules/search/types/advanced-search-state';
import { CalendarAppAdvancedFilters } from '@modules/search/types/calendar-app-advanced-filters';
import { StateService } from '@modules/settings/services/state.service';
import { StateKey } from '@modules/settings/types/state-key';
import { OnChange } from '@frontendlib/common/decorators/on-change';
import { type CalendarEvent } from './calendar-event';

export type EventsListSortField = 'title' | 'startTime' | 'endTime' | 'score';

export class EventsListState extends BaseStitchListStateWithSearch<CalendarEvent, EventsListSortField> {
  @OnChange('onPropertyChange')
  declare public sortBy?: EventsListSortField;
  @OnChange('onPropertyChange')
  declare public today?: boolean;
  @OnChange('onPropertyChange')
  declare public next7days?: boolean;

  constructor({
    searchService,
    stateService,
    stateKey,
    withAdvanced,
    defaultSort = { by: 'startTime', order: SortOrder.ASC },
  }: {
    searchService?: AdvancedSearchService;
    stateService?: StateService;
    stateKey?: StateKey;
    withAdvanced?: boolean;
    defaultSort?: SortBy<EventsListSortField>;
  }) {
    super({ searchService, stateService, stateKey, withAdvanced, defaultSort });
  }

  protected override applySavedState(state?: Like<EventsListState>) {
    super.applySavedState(state);
    // Session-only, never restored: the toggle is drawn in KP alone now, and KP list states carry no
    // `stateKey`. So wherever a saved `true` could still come from, the control that set it is no longer
    // drawn there — restoring one would date-filter a list whose menu offers nothing to turn it back
    // off, leaving "Reset to default" as the only way out. Note this is NOT "the toggle never worked":
    // in four sidebars it did (calendars, groups, folders, notebooks), because their chain never runs
    // `applyAdvancedFilters` and their menus gate it on `withScore` alone. See
    // devdoc/specs/today-filter.md, "Why it is not persisted".
    this.today = false;
    this.next7days = !!state?.next7days;
  }

  public override onPropertyChange(attribute) {
    if ((attribute === 'from' && this.from) || (attribute === 'to' && this.to)) {
      this.today = false;
      this.next7days = false;
    }

    if (attribute === 'today' && this.today) {
      this.from = null;
      this.to = null;
      this.next7days = false;
    }

    if (attribute === 'next7days') {
      this.from = null;
      this.to = null;
      this.today = false;
    }

    super.onPropertyChange(attribute);
  }

  protected override syncState() {
    super.syncState({
      sortBy: this.sortBy,
      next7days: this.next7days,
    });
  }

  public override isDefault(): boolean {
    return super.isDefault() && !this.today && !this.next7days;
  }

  public override hasActiveFilters(): boolean {
    return super.hasActiveFilters() || !!this.today || !!this.next7days;
  }

  public override clearFilters(): void {
    this.today = false;
    this.next7days = false;
    super.clearFilters();
  }

  protected override syncFromAS(advancedSearchState: AdvancedSearchState) {
    const state = advancedSearchState[Application.calendar];
    this.stitchedWith = state.stitched;
    this.from = state.dateRangeFrom;
    this.to = state.dateRangeTo;
  }

  protected override syncToAS(): void {
    const searchState = this.searchService.getStateSync();

    this.searchService.setState(
      new AdvancedSearchState({
        ...searchState,
        calendar: new CalendarAppAdvancedFilters({
          ...searchState.calendar,
          stitched: this.stitchedWith,
          dateRangeFrom: this.from,
          dateRangeTo: this.to,
        }),
      }),
    );
  }
}
